import { useRef, useMemo } from 'react';
import { useAuthSessionInternal } from './auth-provider';
import { useFlowReducer, type FlowReducerConfig } from './use-flow-reducer';
import {
  signUpReducer,
  signUpEffects,
  signUpDeriveFailure,
  isSignUpLoading,
  type SignUpState,
  type SignUpAction,
  type SignUpDeps,
  type SignUpCreateParams,
  type SignUpConfig,
  type SignUpPendingFields,
  type SignUpRequiredField,
} from '../flows/sign-up';
import { consentGateReducer, effectsWithoutDirectRegistration } from '../flows/sign-up/consent-gate';
import { INITIAL_STATE } from '../flows/sign-up/reducer';

export interface UseSignUpReturn {
  state: SignUpState;
  isLoading: boolean;
  dispatch: (action: SignUpAction) => void;

  status: SignUpState['step'];
  email: string | null;
  error: string | null;
  pendingFields: SignUpPendingFields | null;
  requiredFields: SignUpRequiredField[];

  create: (params: SignUpCreateParams) => void;
  attemptVerification: (params: { code: string }) => void;
  submitRequiredFields: (params: { phone?: string; consent?: boolean }) => void;
  resendVerificationCode: () => void;
  back: () => void;
  reset: () => void;
}

export function useSignUp(config?: SignUpConfig): UseSignUpReturn {
  const session = useAuthSessionInternal();
  const configRef = useRef(config);
  configRef.current = config;

  const flowConfig = useMemo((): FlowReducerConfig<SignUpState, SignUpAction, SignUpDeps> => {
    const stableConfig: SignUpConfig = {
      get consentRequired() { return configRef.current?.consentRequired; },
      captureConsent: data => configRef.current?.captureConsent?.(data) ?? Promise.resolve(),
    };

    const reducer = stableConfig.consentRequired ? consentGateReducer : signUpReducer;
    const effects = stableConfig.consentRequired ? effectsWithoutDirectRegistration : signUpEffects;

    return {
      reducer,
      initialState: INITIAL_STATE,
      effects,
      deriveFailure: signUpDeriveFailure,
      deps: {
        service: session.internals.service,
        observability: session.internals.observability,
        eventBus: session.internals.eventBus,
        session,
        config: stableConfig,
      },
    };
  }, [session]);

  const [state, dispatch] = useFlowReducer(flowConfig);

  return {
    state,
    isLoading: isSignUpLoading(state),
    dispatch,

    status: state.step,
    email: 'email' in state ? state.email : null,
    error: 'error' in state ? state.error : null,
    pendingFields: 'pendingFields' in state ? state.pendingFields : null,
    requiredFields: 'requiredFields' in state
      ? (state as Extract<SignUpState, { requiredFields: SignUpRequiredField[] }>).requiredFields
      : [],

    create: params => dispatch({ type: 'SUBMIT_REGISTRATION', params }),
    attemptVerification: params => dispatch({ type: 'SUBMIT_VERIFICATION', code: params.code }),
    submitRequiredFields: params => {
      if (params.consent !== undefined) {
        dispatch({ type: 'SUBMIT_CONSENT', newsletterConsent: params.consent });
      } else if (params.phone) {
        dispatch({ type: 'SUBMIT_PHONE', phone: params.phone });
      }
    },
    resendVerificationCode: () => dispatch({ type: 'RESEND_CODE' }),
    back: () => dispatch({ type: 'BACK' }),
    reset: () => dispatch({ type: 'RESET' }),
  };
}
